/**
 * Select component for dropdown selections
 *
 * Styled with Tailwind CSS using glass morphism for dark theme.
 * Supports validation states (error, success, warning).
 */

import type { SelectOptions, SelectOption, ValidationState } from '../types/form';

/**
 * Base CSS classes for the select element
 */
const BASE_CLASSES = [
  'w-full',
  'px-3',
  'py-2',
  'touch-input',
  'text-sm',
  'text-foreground',
  'rounded-md',
  'border',
  'bg-neutral-950/60',
  'backdrop-blur-sm',
  'transition-colors',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-offset-background',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
  'cursor-pointer',
  'appearance-none',
  'bg-no-repeat',
  'bg-right',
  'pr-10',
].join(' ');

/**
 * CSS classes for validation states
 */
const STATE_CLASSES: Record<ValidationState, string> = {
  default:
    'border-neutral-800 hover:border-neutral-700 focus:border-primary focus:ring-primary/20',
  error:
    'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/20',
  success:
    'border-success hover:border-success focus:border-success focus:ring-success/20',
  warning:
    'border-warning hover:border-warning focus:border-warning focus:ring-warning/20',
};

/**
 * SVG chevron icon for the dropdown (encoded as data URI)
 */
const CHEVRON_ICON =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23a1a1aa' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")";

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `select-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a Select element
 */
export function createSelect(options: SelectOptions): HTMLSelectElement {
  const {
    id = generateId(),
    name,
    options: selectOptions,
    value = '',
    placeholder,
    disabled = false,
    required = false,
    multiple = false,
    size,
    className = '',
    validationState = 'default',
    attributes = {},
    onChange,
    onFocus,
    onBlur,
  } = options;

  const select = document.createElement('select');

  // Set basic attributes
  select.id = id;
  if (name) select.name = name;
  if (disabled) select.disabled = true;
  if (required) select.required = true;
  if (multiple) select.multiple = true;
  if (size !== undefined) select.size = size;

  // Set CSS classes
  const stateClasses = STATE_CLASSES[validationState];
  select.className = `${BASE_CLASSES} ${stateClasses} ${className}`.trim();

  // Set background image for chevron
  select.style.backgroundImage = CHEVRON_ICON;
  select.style.backgroundPosition = 'right 0.5rem center';
  select.style.backgroundSize = '1.5em 1.5em';

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    select.setAttribute(key, val);
  }

  // Add placeholder option if provided
  if (placeholder && !multiple) {
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    placeholderOption.disabled = true;
    placeholderOption.selected = !value;
    placeholderOption.hidden = true;
    select.appendChild(placeholderOption);
  }

  // Group options by group label
  const groups = new Map<string | undefined, SelectOption[]>();
  selectOptions.forEach((opt) => {
    const groupName = opt.group;
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    groups.get(groupName)!.push(opt);
  });

  // Add options
  groups.forEach((opts, groupName) => {
    if (groupName) {
      // Create optgroup
      const optgroup = document.createElement('optgroup');
      optgroup.label = groupName;
      opts.forEach((opt) => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        if (opt.disabled) option.disabled = true;
        if (opt.value === value) option.selected = true;
        optgroup.appendChild(option);
      });
      select.appendChild(optgroup);
    } else {
      // Add options directly
      opts.forEach((opt) => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        if (opt.disabled) option.disabled = true;
        if (opt.value === value) option.selected = true;
        select.appendChild(option);
      });
    }
  });

  // Attach event handlers
  if (onChange) {
    select.addEventListener('change', (event) => {
      onChange(select.value, event);
    });
  }

  if (onFocus) {
    select.addEventListener('focus', (event) => {
      onFocus(event as FocusEvent);
    });
  }

  if (onBlur) {
    select.addEventListener('blur', (event) => {
      onBlur(event as FocusEvent);
    });
  }

  return select;
}

/**
 * Select class for object-oriented usage
 */
export class Select {
  private element: HTMLSelectElement;
  private options: SelectOptions;

  constructor(options: SelectOptions) {
    this.options = { ...options };
    this.element = createSelect(this.options);
  }

  /**
   * Get the select element
   */
  getElement(): HTMLSelectElement {
    return this.element;
  }

  /**
   * Get the current value
   */
  getValue(): string {
    return this.element.value;
  }

  /**
   * Set the value
   */
  setValue(value: string): this {
    this.element.value = value;
    return this;
  }

  /**
   * Get selected values (for multiple select)
   */
  getSelectedValues(): string[] {
    return Array.from(this.element.selectedOptions).map((opt) => opt.value);
  }

  /**
   * Update the options list
   */
  setOptions(selectOptions: SelectOption[]): this {
    // Clear existing options (keep placeholder if exists)
    const hasPlaceholder =
      this.element.options[0]?.disabled && this.element.options[0]?.hidden;
    const startIndex = hasPlaceholder ? 1 : 0;

    while (this.element.options.length > startIndex) {
      this.element.remove(startIndex);
    }

    // Add new options
    selectOptions.forEach((opt) => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label;
      if (opt.disabled) option.disabled = true;
      this.element.appendChild(option);
    });

    this.options.options = selectOptions;
    return this;
  }

  /**
   * Set the validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;
    const stateClasses = STATE_CLASSES[state];

    // Remove all state classes
    Object.values(STATE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new state classes
    stateClasses.split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    return this;
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.element.disabled = disabled;
    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.element.disabled;
  }

  /**
   * Focus the select
   */
  focus(): this {
    this.element.focus();
    return this;
  }

  /**
   * Blur the select
   */
  blur(): this {
    this.element.blur();
    return this;
  }

  /**
   * Check validity
   */
  checkValidity(): boolean {
    return this.element.checkValidity();
  }

  /**
   * Report validity (shows browser validation message)
   */
  reportValidity(): boolean {
    return this.element.reportValidity();
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.removeEventListener(type, listener);
    return this;
  }
}
